import type { FragmentOf } from '~publish/graphql'

import type { SavedReplyItem_SavedReply } from '../../../components/SavedRepliesPopover/SavedReplyItem'
import type { ReplyErrorType } from '../../../types'

export const OPTIMISTIC_REPLY_DURATION = 800
export const SUBMIT_REPLY_KEYBOARD_SHORTCUT = 'Enter'

/**
 * Inserts new text into existing text with proper spacing.
 *
 * @param currentText - The current text content
 * @param newText - The text to insert
 * @returns The combined text with proper spacing
 *
 * @example
 * ```tsx
 * insertText("Hello", "world") // "Hello world"
 * insertText("", "Hello") // "Hello"
 * insertText("Hello ", "world") // "Hello world"
 * ```
 */
export const insertText = (currentText: string, newText: string): string => {
  const trimmedCurrent = currentText.trim()
  return trimmedCurrent ? `${trimmedCurrent} ${newText}` : newText
}

/**
 * Extracts text content from a saved reply.
 *
 * @param savedReply - The saved reply object
 * @returns The text content or empty string if not available
 *
 * @example
 * ```tsx
 * const text = extractSavedReplyText(savedReply) // "Thank you for your feedback!"
 * ```
 */
export const extractSavedReplyText = (
  savedReply: Pick<FragmentOf<typeof SavedReplyItem_SavedReply>, 'content'>,
): string => {
  if (!savedReply) return ''
  return savedReply?.content?.text ?? ''
}

/**
 * Trims and validates reply text for submission.
 *
 * @param text - The raw text input
 * @returns The trimmed text if valid, or null if invalid
 *
 * @example
 * ```tsx
 * validateAndTrimReplyText("  Hello world  ") // "Hello world"
 * validateAndTrimReplyText("   ") // null
 * ```
 */
export const validateAndTrimReplyText = (text: string): string | null => {
  const trimmed = text.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Maps GraphQL error types to internal error types.
 *
 * @param graphqlErrorType - The error type from GraphQL
 * @returns The mapped internal error type
 *
 * @example
 * ```tsx
 * mapErrorType("NotFoundError") // "not_found"
 * ```
 */
export const mapErrorType = (graphqlErrorType?: string): ReplyErrorType => {
  switch (graphqlErrorType) {
    case 'NotFoundError':
      return 'not_found'
    case 'ValidationError':
      return 'validation_error'
    case 'UnauthorizedError':
      return 'unauthorized'
    default:
      return 'api_error'
  }
}

/**
 * Determines if an error indicates a deleted comment.
 *
 * @param error - The error object or message
 * @returns True if the error indicates a deleted comment
 *
 * @example
 * ```tsx
 * isDeletedCommentError("Comment not found") // true
 * isDeletedCommentError("Comment has been deleted") // true
 * isDeletedCommentError("Network error") // false
 * ```
 */
export const isDeletedCommentError = (error: Error | string): boolean => {
  const message = typeof error === 'string' ? error : error.message
  return message.includes('not found') || message.includes('deleted')
}

/**
 * Determines if a keyboard event should trigger form submission.
 *
 * @param event - The keyboard event
 * @param isExpanded - Whether the form is expanded
 * @returns True if the event should trigger submission
 *
 * @example
 * ```tsx
 * const shouldSubmit = shouldSubmitOnKeyPress(event, isExpanded)
 * if (shouldSubmit) {
 *   event.preventDefault()
 *   await submit()
 * }
 * ```
 */
export const shouldSubmitOnKeyPress = (
  event: KeyboardEvent,
  isExpanded: boolean,
): boolean => {
  if (!isExpanded) return false

  const isCmdOrCtrl = event.metaKey || event.ctrlKey
  return isCmdOrCtrl && event.key === SUBMIT_REPLY_KEYBOARD_SHORTCUT
}

/**
 * Validates a form element and reports validation errors to the user.
 *
 * @param formElement - The form element to validate
 * @returns True if the form is valid, false if invalid
 *
 * @example
 * ```tsx
 * const isValid = validateAndReportForm(formRef.current)
 * if (!isValid) {
 *   return // Form validation failed and errors were reported
 * }
 * await submit()
 * ```
 */
export const validateAndReportForm = (
  formElement: HTMLFormElement | null,
): boolean => {
  if (!formElement) return true

  if (!formElement.checkValidity()) {
    formElement.reportValidity()
    return false
  }

  return true
}

/**
 * Formats a username to include the @ symbol if it is not already present.
 * @param username - The username to format
 * @returns The formatted username
 *
 * @example
 * ```tsx
 * formatMentionUsername("testuser") // "@testuser"
 * formatMentionUsername("@testuser") // "@testuser"
 * ```
 */
export const formatMentionUsername = (username: string): string =>
  username.startsWith('@') ? username : `@${username}`
